import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Phone, Clock, Gauge, Timer, Mic2, AlertTriangle, ShieldAlert, Trophy, ListOrdered, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";

const ESITO_COLOR = { positivo: "#34D399", neutro: "#F59E0B", negativo: "#F87171" };
const VAL_COLOR = { buona: "#34D399", migliorabile: "#F59E0B", "non gestita": "#F87171" };
const FREQ_COLOR = { alta: "#F87171", media: "#F59E0B", bassa: "#9CA3AF" };
const fmtDur = (s) => `${Math.floor(s / 60)}m ${s % 60}s`;

export default function CallAnalytics() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/live/analytics").then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, []);

  if (!d) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Analizzo le call…</div>;

  const totEsiti = Object.values(d.esiti).reduce((a, b) => a + b, 0) || 1;
  const totVal = Object.values(d.valutazioni_obiezioni).reduce((a, b) => a + b, 0) || 1;

  return (
    <div data-testid="analytics-page" className="space-y-8">
      <Link to="/live" data-testid="analytics-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4" /> Sessioni live</Link>
      <div className="fade-up">
        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Panoramica</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Le tue call</h1>
        <p className="mt-2 text-sm text-muted-foreground">Errori ricorrenti, tempi morti, obiezioni gestite male: su {d.n_call} call analizzate.</p>
      </div>

      {d.n_call === 0 ? (
        <div data-testid="analytics-empty" className="surface p-10 text-center text-sm text-muted-foreground">Nessuna call analizzata. Termina una sessione live con trascrizione salvata per popolare questa pagina.</div>
      ) : (
        <>
          <div className="stagger grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Stat testId="an-call" label="Call analizzate" value={d.n_call} icon={Phone} accent="#60A5FA" />
            <Stat testId="an-durata" label="Durata media" value={fmtDur(d.durata_media_sec)} icon={Clock} accent="#93C5FD" />
            <Stat testId="an-punteggio" label="Punteggio medio" value={d.punteggio_medio != null ? `${d.punteggio_medio}/10` : "—"} icon={Gauge} accent="#34D399" />
            <Stat testId="an-tempi-morti" label="Tempi morti" value={`${d.tempi_morti.count} · ${fmtDur(d.tempi_morti.total_sec)}`} icon={Timer} accent="#F59E0B" />
            <Stat testId="an-talk" label="Tuo tempo di parola" value={d.talk_ratio_medio != null ? `${d.talk_ratio_medio}%` : "n/d"} icon={Mic2} accent="#FCD990" hint={d.talk_ratio_medio == null ? "Segna la tua voce durante la call" : null} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Bars testId="an-esiti" title="Esiti" data={d.esiti} total={totEsiti} colors={ESITO_COLOR} />
            <Bars testId="an-valutazioni" title="Gestione delle obiezioni" data={d.valutazioni_obiezioni} total={totVal} colors={VAL_COLOR} />
          </div>

          {d.sintesi ? (
            <div className="stagger grid gap-6 lg:grid-cols-2">
              <Block icon={Sparkles} title="Sintesi del coach" testId="an-sintesi" className="lg:col-span-2"><p className="text-sm leading-relaxed">{d.sintesi.sintesi}</p></Block>
              <Block icon={AlertTriangle} title="Errori più frequenti" testId="an-errori">
                <ul className="space-y-3">
                  {(d.sintesi.errori_frequenti || []).map((e, i) => (
                    <li key={i} className="rounded-lg bg-background p-3 text-sm">
                      <div className="flex items-start justify-between gap-3"><p className="font-medium">{e.errore}</p><span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-mono" style={{ background: `${FREQ_COLOR[e.frequenza] || "#9CA3AF"}22`, color: FREQ_COLOR[e.frequenza] || "#9CA3AF" }}>{e.frequenza}</span></div>
                      <p className="mt-1 text-xs text-muted-foreground">{e.consiglio}</p>
                    </li>
                  ))}
                </ul>
              </Block>
              <Block icon={ShieldAlert} title="Obiezioni mal riuscite" testId="an-obiezioni">
                <ul className="space-y-3">
                  {(d.sintesi.obiezioni_critiche || []).map((o, i) => (
                    <li key={i} className="rounded-lg bg-background p-3 text-sm">
                      <div className="flex items-start justify-between gap-3"><p className="font-medium">{o.obiezione}</p><span className="shrink-0 font-mono text-[11px] text-muted-foreground">×{o.occorrenze}</span></div>
                      <p className="mt-1 text-xs text-red-300/80">{o.problema}</p>
                      <p className="mt-1 text-xs text-muted-foreground">→ {o.come_migliorare}</p>
                    </li>
                  ))}
                </ul>
              </Block>
              <Block icon={Trophy} title="Punti di forza" testId="an-forza"><Bullets items={d.sintesi.punti_di_forza} color="#34D399" /></Block>
              <Block icon={ListOrdered} title="Priorità di miglioramento" testId="an-priorita">
                <ol className="space-y-2 text-sm">{(d.sintesi.priorita || []).map((p, i) => <li key={i} className="flex gap-3"><span className="font-mono text-primary">{i + 1}.</span><span>{p}</span></li>)}</ol>
              </Block>
            </div>
          ) : (
            <div className="surface p-6 text-sm text-muted-foreground">Sintesi del coach non disponibile al momento.</div>
          )}

          <Block icon={Mic2} title={`Errori dialettici rilevati (${d.errori_dialettici.length})`} testId="an-errori-dialettici">
            {d.errori_dialettici.length === 0 ? <p className="text-sm text-muted-foreground">Nessun errore dialettico rilevato nelle call analizzate.</p> : (
              <ul className="grid gap-3 md:grid-cols-2">
                {d.errori_dialettici.map((e, i) => (
                  <li key={i} className="rounded-lg bg-background p-3 text-sm">
                    <p className="font-medium">{e.errore}</p>
                    {e.esempio && <p className="mt-1 text-xs italic text-muted-foreground">“{e.esempio}”</p>}
                    {e.correzione && <p className="mt-1 text-xs text-emerald-300/80">→ {e.correzione}</p>}
                    <Link to={`/live/${e.session_id}`} className="mt-2 inline-block text-[11px] font-mono text-primary hover:underline">vai alla call</Link>
                  </li>
                ))}
              </ul>
            )}
          </Block>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, accent, testId, hint }) => (
  <div data-testid={testId} className="surface surface-hover min-w-0 p-5">
    <div className="flex items-center justify-between"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p><Icon className="h-4 w-4" style={{ color: accent }} /></div>
    <p className="mt-3 truncate font-mono text-2xl font-bold tracking-tight">{value}</p>
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const Bars = ({ title, data, total, colors, testId }) => (
  <div data-testid={testId} className="surface space-y-3 p-5">
    <h2 className="text-sm font-semibold">{title}</h2>
    {Object.entries(data).map(([k, v]) => (
      <div key={k}>
        <div className="flex items-center justify-between text-xs"><span className="capitalize">{k}</span><span className="font-mono text-muted-foreground">{v}</span></div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${(v / total) * 100}%`, background: colors[k] }} /></div>
      </div>
    ))}
  </div>
);

const Block = ({ icon: Icon, title, testId, className = "", children }) => (
  <section data-testid={testId} className={`surface min-w-0 p-5 ${className}`}>
    <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-primary" />{title}</h2>
    {children}
  </section>
);

const Bullets = ({ items = [], color = "#2563EB" }) => items.length === 0 ? <p className="text-sm text-muted-foreground">—</p> : (
  <ul className="space-y-2 text-sm">{items.map((t, i) => <li key={i} className="flex gap-2"><span style={{ color }}>•</span><span>{t}</span></li>)}</ul>
);
